import React from "react";
import {useTranslation} from "react-i18next";
import RowResumeText from "../../../../../components/RowResumeText";
import ResumeInfoCard from "../../../../../components/ResumeInfoCard";
import NotInformation from "../../../../../components/NotInformation";
import PhotoPickerSummary from "../../../../../components/PhotoPicker/PhotoPickerSummary";
import {DocumentLinesIcon, DocumentationFolderIcon, FileChip, IconAreaTable, UseCameraIcon} from "../../../../../components/buttons/IconButtons";
import {EMPTY_VALUES, formatBytes, getDateFormat} from "../../../../../Apis/functions";
import SummaryDetailsCard from "../../../Company/Employees/EnrollmentSteps/SubComponents/SummaryDetailsCard";
import SummaryFingerprintCard from "../../../Company/Employees/EnrollmentSteps/SubComponents/SummaryFingerprintCard";
import {summaryHasOverflow, summaryHiddenItems, summaryVisibleItems} from "../../Model/Model";
import SummaryOverflowItem from "../../SummaryOverflowItem";

/**
 * Review of the guard before it is created: photo, details, contract, documents and fingerprints.
 */
const GuardSummaryStep = ({userData, employeeData, zoneAccesses, zoneAccessIds, documents, photoPicker, fingerprints}) => {
    const {t} = useTranslation();
    const selectedZoneAccesses = zoneAccesses.filter((item) => zoneAccessIds.includes(item.id));
    const toDate = (date) => date ? getDateFormat(typeof date === 'number' ? date : date.getTime(), false) : EMPTY_VALUES.DEFAULT;

    return (
        <div className='row mt-3'>
            <div className={"row mb-4 px-0"}>
                <div className={`background-general-00 border-radius-8px border-full-shades-300 p-0`}>
                    <div className={'p-3 d-flex align-items-center'}>
                        <div className={'mr-2'}>
                            <UseCameraIcon width={24} height={24}/>
                        </div>
                        <p className={'font-color-general-950 ibl-font-w-600 fs-14-to-13 '}>{t('photography')}</p>
                    </div>
                    <div className={"border-bottom-shades-300 w-100"}/>
                    <div className={"p-3"}>
                        <PhotoPickerSummary photoPicker={photoPicker} classWith={'w-400px'}/>
                    </div>
                </div>
            </div>

            <div className={"row mb-4 px-0"}>
                <SummaryDetailsCard userData={userData}/>
            </div>

            <div className={"row mb-4 px-0"}>
                <ResumeInfoCard
                    title={t('contract')}
                    titleIcon={<DocumentationFolderIcon width={24} height={24}/>}
                    icon={<DocumentLinesIcon className='btn-icon-svg-24px'/>}
                    iconClass='background-primary-100'
                    contentClass={"py-3"}
                    body={
                        <>
                            <div className='grid-container-resume-data mb-3'>
                                <RowResumeText
                                    className='d-flex flex-column p-2 background-shades-50 border-radius-8px'
                                    title={t('start_date')}
                                    value={toDate(employeeData?.startDate)}
                                />
                                <RowResumeText
                                    className='d-flex flex-column p-2 background-shades-50 border-radius-8px'
                                    title={t('end_date')}
                                    value={toDate(employeeData?.endDate)}
                                />
                            </div>
                            {/* Each zone access the guard watches, as a card of its own */}
                            <div className='mb-2'>
                                <h6 className='mb-2 ibl-font-w-600 fs-14px font-color-general-950'>{t('zone_access')}</h6>
                                {selectedZoneAccesses.length > 0 ? (
                                    <div className='grid-container-resume-data'>
                                        {selectedZoneAccesses.map((zoneAccess) => (
                                            <div key={zoneAccess.id} className='d-flex align-items-center p-2 background-shades-50 border-radius-8px'>
                                                <IconAreaTable width={20} height={20} className='icon-color-general-950 mr-8px flex-shrink-0'/>
                                                <span className='fs-14px ibl-font-w-600 font-color-general-950 text-truncate'>
                                                    {zoneAccess.name || EMPTY_VALUES.DEFAULT}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <NotInformation text={t('nothing_selected_yet')} card={true}/>
                                )}
                            </div>
                        </>
                    }
                />
            </div>

            <div className={"row mb-4 px-0"}>
                <ResumeInfoCard
                    title={t('documents')}
                    titleIcon={<DocumentationFolderIcon width={24} height={24}/>}
                    icon={<FileChip className='btn-icon-svg-24px'/>}
                    iconClass='background-primary-100'
                    contentClass={"py-3"}
                    body={
                        documents?.length > 0 ? (
                            // Each document shows its name over its size, as the details do
                            <div className='grid-container-resume-data mb-2'>
                                {summaryVisibleItems(documents).map((document) => (
                                    <RowResumeText
                                        key={document.id}
                                        className='d-flex flex-column p-2 background-shades-50 border-radius-8px'
                                        title={document.name || EMPTY_VALUES.DEFAULT}
                                        value={document.file?.size ? formatBytes(document.file.size, 1) : EMPTY_VALUES.DEFAULT}
                                    />
                                ))}
                                {summaryHasOverflow(documents) &&
                                    <SummaryOverflowItem label={'total_added_documents'} hiddenItems={summaryHiddenItems(documents).map((document) => ({label: document.name, value: document.file?.size ? formatBytes(document.file.size, 1) : ''}))}/>
                                }
                            </div>
                        ) : (
                            <NotInformation text={t('no_documents_uploaded')} card={true}/>
                        )
                    }
                />
            </div>

            <div className={"row mb-4 px-0"}>
                <SummaryFingerprintCard
                    mergedFingerprints={fingerprints}
                    emptyText={'no_fingerprints_added_yet'}
                />
            </div>
        </div>
    );
};

export default GuardSummaryStep;
